// Pdfium WASM loader — uses @pdfking/wasm-core for generic glue

import type { WasmExportConfig } from "@pdfking/wasm-core"
import type {
  PdfiumArguments,
  PdfiumFunctions,
  PdfiumReturns,
} from "../types/Bindings"
import type { PdfiumApiFactory } from "../types/PdfiumApi"
import {
  PdfiumExports as E,
  IMPORT_MODULE,
  pdfium as wasm,
} from "@pdfking/pdfium"
import { GuidToString, IsString, LockManager, UUID } from "@pdfking/tfunc"
import { CreateWasmLoader, Stub } from "@pdfking/wasm-core"

const fail = (error: Error | string): never => {
  throw IsString(error) ? new Error(error) : error
}

const ERRORS: Record<number, string> = {
  1: "unknown error",
  2: "file not found or could not be opened",
  3: "file not in PDF format or corrupted",
  4: "password required or incorrect password",
  5: "unsupported security scheme",
  6: "page not found or content error",
}

const PDFIUM_CONFIG: WasmExportConfig = {
  importModule: IMPORT_MODULE,
  memory: E.memory,
  malloc: E.malloc,
  free: E.free,
  indirectFunctionTable: E.__indirect_function_table,
  wasmCallCtors: E.__wasm_call_ctors,
  setThrew: E.setThrew,
  stackGetCurrent: E.emscripten_stack_get_current,
  stackRestore: E._emscripten_stack_restore,
  invokes: {
    [E.invoke_ii]: true,
    [E.invoke_iii]: true,
    [E.invoke_iiii]: true,
    [E.invoke_iiiii]: true,
    [E.invoke_viii]: false,
  },
  stubs: {
    [E._tzset_js]: Stub.noop,
    [E._localtime_js]: Stub.noop,
    [E._gmtime_js]: Stub.noop,
    [E.__syscall_fcntl64]: Stub.zero,
    [E.__syscall_ioctl]: Stub.zero,
    [E.__syscall_openat]: Stub.enoent,
    [E.__syscall_stat64]: Stub.enoent,
    [E.__syscall_newfstatat]: Stub.enoent,
    [E.__syscall_lstat64]: Stub.enoent,
    [E.__syscall_unlinkat]: Stub.enoent,
    [E.__syscall_rmdir]: Stub.enoent,
    [E.__syscall_fstat64]: Stub.ebadf_neg,
    [E.__syscall_ftruncate64]: Stub.ebadf_neg,
    [E.__syscall_getdents64]: Stub.ebadf_neg,
    [E._emscripten_runtime_keepalive_clear]: Stub.noop,
    [E._setitimer_js]: Stub.zero,
    [E.fd_close]: Stub.ebadf,
    [E.fd_write]: Stub.ebadf,
    [E.fd_read]: Stub.ebadf,
    [E.fd_seek]: Stub.ebadf,
    [E.fd_sync]: Stub.zero,
    [E.environ_get]: Stub.zero,
    [E.emscripten_resize_heap]: Stub.resize_heap,
    [E.emscripten_date_now]: Stub.date_now,
    [E._abort_js]: Stub.abort,
    [E._emscripten_throw_longjmp]: Stub.throw_longjmp,
    [E.__cxa_throw]: Stub.cxa_throw,
    [E.__assert_fail]: Stub.abort,
    [E.environ_sizes_get]: Stub.environ_sizes_get,
    [E.proc_exit]: Stub.exit,
  },
  onInit: exports => {
    const initLib = exports[E.FPDF_InitLibrary] as () => void
    initLib()
  },
}

const LoadPdfiumWasm = async (url: string) => {
  const createInstance = await CreateWasmLoader(url, PDFIUM_CONFIG)
  const { schedule } = LockManager()

  return async () => {
    const id = GuidToString(UUID())
    const instance = await createInstance()

    const getLastError = async (): Promise<Error | undefined> => {
      const code = await call(E.FPDF_GetLastError)
      return ERRORS[code] ? new Error(ERRORS[code]) : undefined
    }

    const call = async <M extends PdfiumFunctions>(
      method: M,
      ...args: PdfiumArguments[M]
    ): Promise<PdfiumReturns[M]> => {
      const fn = instance.exports[method] as
        | ((...a: number[]) => number)
        | undefined
      if (!fn) return fail(`Function ${method} not found`)
      return schedule(id, async () => fn(...args)) as Promise<PdfiumReturns[M]>
    }

    const safeCall = async <M extends PdfiumFunctions>(
      method: M,
      ...args: PdfiumArguments[M]
    ): Promise<PdfiumReturns[M]> => {
      const result = await call(method, ...args)
      if (!result) fail((await getLastError()) || `Failed: ${method}`)
      return result
    }

    return { ...instance, call, id, safeCall }
  }
}

export const CreatePdfiumFactory: PdfiumApiFactory = (path?: string) =>
  LoadPdfiumWasm(path || wasm)
